import { Body, Button, Icon, Tooltip } from '@assembly-js/design-system';
import { Grid, Skeleton } from '@mui/material';
import { Formik, FormikProps } from 'formik';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';
import Stripe from 'stripe';
import { makeStyles } from 'tss-react/mui';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import UsersClient from 'src/clients/UsersClient';
import Autocomplete from 'src/components/Autocomplete';
import { ACCOUNT_TYPE } from 'src/constants';
import { PortalConfigContext, useFeatureFlags } from 'src/context';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { BaseTextField } from 'src/legacy/components/TextField';
import { useCreateStripeExpressAccountMutation } from 'src/services/api/appApi';
import { alertSnackbar } from 'src/store/ui/actions';
import { GraySmall } from 'src/theme/colors';
import { GetStripeAccountVerificationStatus } from 'src/utils/BillingUtils';

import { browserLocales, resolveInitialExpressCountry } from './expressCountry';
import { AccountStatusData, VerificationStatus } from './VerificationStatus';

interface CountryOptionType {
  code: string;
  label: string;
}

const expressCoOptions: CountryOptionType[] = [
  { label: 'Australia', code: 'AU' },
  { label: 'Canada', code: 'CA' },
  { label: 'United Kingdom', code: 'GB' },
  { label: 'United States', code: 'US' },
];

// All Stripe Express supported countries
const allExpressCoOptions: CountryOptionType[] = [
  { label: 'Australia', code: 'AU' },
  { label: 'Austria', code: 'AT' },
  { label: 'Belgium', code: 'BE' },
  { label: 'Brazil', code: 'BR' },
  { label: 'Bulgaria', code: 'BG' },
  { label: 'Canada', code: 'CA' },
  { label: 'Croatia', code: 'HR' },
  { label: 'Cyprus', code: 'CY' },
  { label: 'Czech Republic', code: 'CZ' },
  { label: 'Denmark', code: 'DK' },
  { label: 'Estonia', code: 'EE' },
  { label: 'Finland', code: 'FI' },
  { label: 'France', code: 'FR' },
  { label: 'Germany', code: 'DE' },
  { label: 'Greece', code: 'GR' },
  { label: 'Hong Kong', code: 'HK' },
  { label: 'Hungary', code: 'HU' },
  { label: 'Ireland', code: 'IE' },
  { label: 'Italy', code: 'IT' },
  { label: 'Japan', code: 'JP' },
  { label: 'Latvia', code: 'LV' },
  { label: 'Lithuania', code: 'LT' },
  { label: 'Luxembourg', code: 'LU' },
  { label: 'Malta', code: 'MT' },
  { label: 'Mexico', code: 'MX' },
  { label: 'Netherlands', code: 'NL' },
  { label: 'New Zealand', code: 'NZ' },
  { label: 'Norway', code: 'NO' },
  { label: 'Poland', code: 'PL' },
  { label: 'Portugal', code: 'PT' },
  { label: 'Romania', code: 'RO' },
  { label: 'Singapore', code: 'SG' },
  { label: 'Slovakia', code: 'SK' },
  { label: 'Slovenia', code: 'SI' },
  { label: 'Spain', code: 'ES' },
  { label: 'Sweden', code: 'SE' },
  { label: 'Switzerland', code: 'CH' },
  { label: 'Thailand', code: 'TH' },
  { label: 'United Arab Emirates', code: 'AE' },
  { label: 'United Kingdom', code: 'GB' },
  { label: 'United States', code: 'US' },
];

const useStyles = makeStyles()((theme) => ({
  formContainer: {
    marginTop: theme.spacing(2),
  },
  connectButtonContainer: {
    marginTop: theme.spacing(4.5),
    display: 'flex',
    justifyContent: 'flex-start',
  },
}));

const connectSchema = (allowedCodes: readonly string[]) =>
  z.object({
    businessName: z.string(),
    accountType: z.enum([ACCOUNT_TYPE.EXPRESS]),
    country: z
      .string()
      .refine(
        (code) => allowedCodes.includes(code),
        'Select a billing country.',
      ),
  });

type Schema = z.infer<ReturnType<typeof connectSchema>>;

export const StripeConnectForm: React.FC = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const portalConfig = useContext(PortalConfigContext);
  const { EnableAdditionalExpressOnboardingOptions, EnableNewSettings } =
    useFeatureFlags();
  const countryOptions = EnableAdditionalExpressOnboardingOptions
    ? allExpressCoOptions
    : expressCoOptions;
  const payoutsBlocked = portalConfig.disableAllEmailNotifications === true;
  const dispatch = useAppDispatch();
  const [createStripeExpressAccount] = useCreateStripeExpressAccountMutation();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { loading, cognitoUser, paymentInfo } = useAppSelector(
    (state) => ({
      loading: state.settings.loading,
      cognitoUser: state.user.instance,
      paymentInfo: state.payments.paymentInfo,
    }),
    shallowEqual,
  );
  const backToPortalDestinationPath = useAppSelector(
    (state) => state.ui.backToPortalDestinationPath,
  );

  const {
    address: paymentAddress,
    businessName,
    accountId,
  } = paymentInfo || {};

  const allowedCountryCodes = countryOptions.map((option) => option.code);
  const supportedCountryCodes = allExpressCoOptions.map(
    (option) => option.code,
  );
  // Connect Express submits this form with the country picker hidden, so an
  // existing Stripe country has to stay valid even when the short list hides it.
  const schema = connectSchema(
    accountId ? supportedCountryCodes : allowedCountryCodes,
  );

  const [accountStatus, setAccountStatus] = React.useState<AccountStatusData>({
    accountType: '',
    loginUrl: '',
    verificationStatus: '',
    accountName: '',
    currency: '',
    accountCountry: '',
    businessName: '',
  });

  const [isVerifying, setIsVerifying] = React.useState(true);
  const [hasLoadError, setHasLoadError] = React.useState(false);

  const country = resolveInitialExpressCountry(
    [paymentAddress?.country, accountStatus.accountCountry],
    browserLocales(),
    allowedCountryCodes,
    supportedCountryCodes,
  );

  const checkAccount = async (stripeAccountId: string) => {
    if (stripeAccountId === 'restricted') {
      // if accountID is restricted, then we don't need load account and check
      // any further status and directly set status to restricted
      setAccountStatus({
        accountType: '',
        loginUrl: '',
        verificationStatus: 'restricted',
        accountName: '',
        currency: '',
        accountCountry: '',
        businessName: '',
      });
      setIsVerifying(false);
      return;
    }

    setIsVerifying(true);
    setHasLoadError(false);
    let result: Awaited<ReturnType<typeof UsersClient.getAccount>>;
    try {
      result = await UsersClient.getAccount(stripeAccountId);
    } catch (e) {
      // Left unhandled, the form stays on its skeleton for good and the
      // account can't be seen or reconnected without a reload.
      setHasLoadError(true);
      setIsVerifying(false);
      return;
    }
    if (result && result.data) {
      let stripeLoginLink = '';
      const {
        payouts_enabled,
        type,
        requirements,
        external_accounts,
        default_currency,
        country: accountCountry,
        business_profile,
        charges_enabled,
        settings,
      } = result.data;

      if (type === ACCOUNT_TYPE.EXPRESS) {
        try {
          const loginUrlResult =
            await UsersClient.getAccountLoginLink(stripeAccountId);
          if (loginUrlResult && loginUrlResult.data) {
            stripeLoginLink = loginUrlResult.data.url;
          }
        } catch (e) {
          setIsVerifying(false);
        }
      }

      let accountName = 'Connected accounts';
      if (external_accounts && external_accounts.data.length > 0) {
        const [externalAccount] = external_accounts.data;
        if (externalAccount.object === 'bank_account') {
          accountName = externalAccount.bank_name || 'Bank';
        } else if (externalAccount.object === 'card') {
          accountName = externalAccount.brand;
        }
      }
      const verificationStatus = GetStripeAccountVerificationStatus(
        payouts_enabled,
        charges_enabled,
        requirements as Stripe.Account.Requirements,
      );

      if (verificationStatus === 'onboarding') {
        try {
          const onboardingLink =
            await UsersClient.getAccountOnboardingLink(stripeAccountId);
          if (onboardingLink && onboardingLink.data) {
            stripeLoginLink = onboardingLink.data.url;
          }
        } catch (e) {
          console.error('Error getting onboarding link', e);
        }
      }

      setAccountStatus({
        accountType: type,
        loginUrl: stripeLoginLink,
        verificationStatus,
        accountName,
        currency: default_currency || '',
        accountCountry: accountCountry || '',
        businessName: business_profile?.name || '',
        accountLast4: external_accounts?.data.at(0)?.last4,
        payoutSchedule: settings?.payouts?.schedule,
      });
    }
    setIsVerifying(false);
  };

  React.useEffect(() => {
    if (accountId && !loading) {
      checkAccount(accountId);
    } else {
      setAccountStatus({
        accountType: '',
        loginUrl: '',
        verificationStatus: '',
        accountName: '',
        currency: '',
        accountCountry: '',
        businessName: '',
      });
      setIsVerifying(false);
    }
  }, [accountId, loading]);

  const renderFormFields = ({
    errors,
    handleBlur,
    handleChange,
    touched,
    values,
    setFieldValue,
  }: FormikProps<Schema>) => (
    <div className={classes.formContainer}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <BaseTextField
            sizeVariant="medium"
            error={Boolean(touched.businessName && errors.businessName)}
            fullWidth
            helperText={touched.businessName && errors.businessName}
            label={t(($) => $.page_companies.registered_company_name)}
            name="businessName"
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.businessName}
            variant="outlined"
            placeholder="Infinity & Beyond LLC"
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            id="country-code-select"
            onChange={(_, value) =>
              setFieldValue('country', (value as CountryOptionType)?.code || '')
            }
            value={
              countryOptions.find((option) => option.code === values.country) ||
              null
            }
            options={countryOptions}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <BaseTextField
                {...params}
                className="[&_.MuiAutocomplete-popupIndicator_.MuiSvgIcon-root]:h-6! [&_.MuiAutocomplete-popupIndicator_.MuiSvgIcon-root]:w-6! [&_.MuiAutocomplete-clearIndicator_.MuiSvgIcon-root]:h-5! [&_.MuiAutocomplete-clearIndicator_.MuiSvgIcon-root]:w-5! [&_.MuiSvgIcon-root]:text-inherit!"
                sizeVariant="medium"
                error={Boolean(touched.country && errors.country)}
                fullWidth
                data-testid="country-select-id"
                helperText={touched.country && errors.country}
                label={
                  <>
                    <BaseTypography fontType="13Medium" component="span">
                      Country
                    </BaseTypography>
                    <BaseTypography
                      component="span"
                      style={{ color: GraySmall }}
                    >
                      {' '}
                      - The currency of your bank account will be used for
                      billing clients
                    </BaseTypography>
                  </>
                }
                onBlur={handleBlur}
                variant="outlined"
                placeholder="Select billing country"
                inputProps={{
                  ...params.inputProps,
                  isautocomplete: 'true',
                }}
              />
            )}
          />
        </Grid>
      </Grid>
    </div>
  );

  const renderFormGrid = (formProps: FormikProps<Schema>) => (
    <>
      {renderFormFields(formProps)}
      <div className={classes.connectButtonContainer}>
        {payoutsBlocked ? (
          <Tooltip content={t(($) => $.payout_account.ineligible)}>
            <span>
              <Button
                label="Connect bank"
                type="submit"
                loading={isSubmitting}
                disabled
              />
            </span>
          </Tooltip>
        ) : (
          <Button
            label="Connect bank"
            type="submit"
            loading={isSubmitting}
            disabled={loading}
          />
        )}
      </div>
    </>
  );

  // Stripe fixes an account's country, and so its currency, when the account
  // is created, so the new-settings empty state asks for both inline before
  // sending the owner to Stripe.
  const renderConnectEmptyState = (formProps: FormikProps<Schema>) => {
    const connectButton = (
      <Button
        type="submit"
        label={t(($) => $.payout_account.connect_bank_account)}
        loading={isSubmitting}
        disabled={payoutsBlocked || loading || isSubmitting}
      />
    );

    return (
      <div className="flex flex-col gap-6 py-6">
        <div className="flex flex-col items-center gap-1 text-center">
          <div className="mb-2 grid h-10 w-10 place-items-center rounded-[4px] bg-gray-100 text-gray-small">
            <Icon icon="credit-card" className="h-5 w-5" />
          </div>
          <Body
            tag="div"
            className="font-medium leading-[22px] text-black-heading"
          >
            {t(($) => $.payout_account.empty_title)}
          </Body>
          <Body size="sm" tag="div" className="leading-[20px] text-gray-small">
            {t(($) => $.payout_account.empty_description)}
          </Body>
        </div>
        <div>
          {renderFormFields(formProps)}
          <Body
            size="sm"
            tag="div"
            className="mt-3 leading-[20px] text-gray-small"
          >
            {t(($) => $.payout_account.connect_description)}
          </Body>
          <div className="mt-4 flex justify-end">
            {payoutsBlocked ? (
              <Tooltip content={t(($) => $.payout_account.ineligible)}>
                <span>{connectButton}</span>
              </Tooltip>
            ) : (
              connectButton
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleSubmit = async (values: Schema) => {
    if (!cognitoUser || payoutsBlocked) {
      return;
    }

    setIsSubmitting(true);
    const portalId = portalConfig.portalHeader;
    const next = `${window.location.origin}${backToPortalDestinationPath}`;
    const nextUrl = `${portalConfig.BaseAPIUrl}/users/stripe/complete-onboarding?portalId=${encodeURIComponent(portalId)}&next=${encodeURIComponent(next)}`;

    try {
      const result = await createStripeExpressAccount({
        businessName: values.businessName,
        country: values.country,
        email: cognitoUser.attributes.email,
        businessUrl: portalConfig.SignoutRedirectURL,
        nextUrl,
      }).unwrap();
      window.location.href = result.onboardingUrl;
    } catch (error) {
      dispatch(
        alertSnackbar({
          errorMessage: 'Failed to create Stripe account. Please try again.',
        }),
      );

      setIsSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        businessName: accountStatus.businessName || businessName || '',
        country,
        accountType: ACCOUNT_TYPE.EXPRESS,
      }}
      validationSchema={toFormikValidationSchema(schema)}
      onSubmit={handleSubmit}
    >
      {(formProps) =>
        loading || isVerifying ? (
          <Skeleton variant="text" />
        ) : hasLoadError && accountId ? (
          <div
            data-testid="payout-account-load-error"
            className="flex items-center justify-between gap-4"
          >
            <Body size="sm" className="font-medium text-gray-small">
              {t(($) => $.payout_account.load_error)}
            </Body>
            <Button
              type="button"
              variant="secondary"
              label={t(($) => $.payout_account.retry)}
              onClick={() => void checkAccount(accountId)}
            />
          </div>
        ) : (
          <form onSubmit={formProps.handleSubmit}>
            {!accountId ? (
              EnableNewSettings ? (
                renderConnectEmptyState(formProps)
              ) : (
                renderFormGrid(formProps)
              )
            ) : (
              <VerificationStatus
                statusData={accountStatus}
                onConnectExpressClick={formProps.submitForm}
                payoutsBlocked={payoutsBlocked}
              />
            )}
          </form>
        )
      }
    </Formik>
  );
};
